import type { ReactNode } from 'react';
import { formatDate } from '../../utils/dateFormatter';

export type EventColor =
  | 'blue'
  | 'gray'
  | 'green'
  | 'orange'
  | 'red'
  | 'purple'
  | 'indigo'
  | 'cyan'
  | 'violet';

const COLOR_CLASSES: Record<EventColor, string> = {
  blue: 'bg-blue-500/10 text-blue-500',
  gray: 'bg-gray-500/10 text-gray-500',
  green: 'bg-green-500/10 text-green-500',
  orange: 'bg-orange-500/10 text-orange-500',
  red: 'bg-red-500/10 text-red-500',
  purple: 'bg-purple-500/10 text-purple-500',
  indigo: 'bg-indigo-500/10 text-indigo-500',
  cyan: 'bg-cyan-500/10 text-cyan-500',
  violet: 'bg-violet-500/10 text-violet-500',
};

interface EventShellProps {
  icon: ReactNode;
  color: EventColor;
  title: string;
  timestamp: string;
  children?: ReactNode;
}

/**
 * Shared timeline row for a single journey event. Renders the colored icon
 * node (which sits on the parent's connector line) plus the title, a
 * right-aligned timestamp, and any event-specific detail content.
 */
export default function EventShell({ icon, color, title, timestamp, children }: EventShellProps) {
  return (
    <div className="group flex gap-3">
      <div
        className={`relative z-10 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full shadow-sm ring-1 ring-inset ring-current/10 ${COLOR_CLASSES[color]}`}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <div className="flex items-baseline justify-between gap-3">
          <h4 className="text-[13px] font-medium leading-5 text-foreground">{title}</h4>
          <time className="flex-shrink-0 text-[10px] tabular-nums text-muted-foreground/70">
            {formatDate(timestamp, 'compact')}
          </time>
        </div>
        {children && (
          <div className="mt-1 space-y-0.5 break-words text-[11px] leading-4 text-muted-foreground">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
